import { pipe } from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as A from "fp-ts/Array"

import { DEV_FLAGS } from "@app/config/dev-flags"
import { OrganizationTier } from "@app/helpers/admin/backend/enterprise-types"
import { diag } from "@hoppscotch/common/kernel/log"

// URL is the source of truth for org context. On desktop, the Rust load
// command sets ?org=test-org.hoppscotch.io on the initial webview URL, and
// a Vue Router beforeEach guard (modules/router.ts) preserves it across all
// internal navigations and reloads. On web, the org context comes from
// window.location.hostname as usual.
declare global {
  interface Window {
    __KERNEL_MODE__?: "web" | "desktop"
  }
}

export const API_DOCS_DOMAINS = [
  "api-docs.hoppscotch.io",
  "api-docs.deploychan.hoppscotch.com",
]

// These are the hostnames where the org sidebar doesn't show up. The default cloud
// instance (hoppscotch.io) and any localhost/staging URLs live here. Everything else
// gets treated as an org subdomain.
const DEFAULT_CLOUD_HOSTNAMES = [
  "hoppscotch.io",
  "hoppscotch",
  "localhost",
  "default.hoppscotch.io",
  "cloud-staging.deploychan.hoppscotch.com",
  "cloud-orgs-staging.deploychan.hoppscotch.com",
]

// PR deploy previews: {pr-number}.cloud-staging.deploychan.hoppscotch.com
const isPRDeployPreview = (hostname: string): boolean =>
  /^\d+\.(cloud-staging|cloud-orgs-staging)\.deploychan\.hoppscotch\.com$/.test(
    hostname
  )

// Checks `window.__KERNEL_MODE__` rather than protocol because it's more reliable
// across different webview contexts. The kernel mode gets set during initialization and
// doesn't change during runtime, so it's a stable way to determine platform.
export const isDesktopMode = (): boolean => window.__KERNEL_MODE__ === "desktop"

// True when the org's tier is ENTERPRISE, or when the backend returned
// `sso/required` before the tier could be read.
export const isEnterpriseOrg = (
  orgInfo: { tier?: string | null; error?: string | null } | null | undefined
): boolean =>
  orgInfo?.tier === OrganizationTier.ENTERPRISE ||
  !!orgInfo?.error?.includes("sso/required")

// the raw org host (e.g. "test-org.hoppscotch.io") from the ?org= query param.
// on desktop, the Rust side sets this on the initial URL and the router guard
// preserves it across all navigations. on web this returns null (no ?org= param).
// must be defined before getEffectiveHostname since isDefaultCloudInstance
// evaluates getEffectiveHostname at module load time
const desktopOrgHost = (): string | null =>
  new URLSearchParams(window.location.search).get("org")

// desktop uses underscores in hostnames (acme_hoppscotch_io) because dots aren't
// valid in the app:// protocol. this converts back to dots so the rest of the
// codebase can work with standard domain patterns. the normalization happens at
// this boundary point so everything downstream sees "acme.hoppscotch.io"
// regardless of whether it's web or desktop
//
// on Windows, Tauri's custom protocol gets rewritten by WebView2, the app://
// scheme becomes an http:// subdomain of the virtual host, so
// app://acme_hoppscotch_io/ becomes http://app.acme_hoppscotch_io/ and
// window.location.hostname is "app.acme_hoppscotch_io" instead of just
// "acme_hoppscotch_io". we strip the "app." prefix on desktop before doing
// underscore replacement so both platforms normalize to the same result
//
// see the WHITELISTED_ORIGINS comment in .env.example for the full breakdown
// of per-platform origin formats
export const getEffectiveHostname = (): string => {
  if (isDesktopMode()) {
    // org webviews pass the real host as a query param (?org=test-org.hoppscotch.io)
    // since all webviews share the same app://hoppscotch origin for IPC compat.
    // when present, use it directly as the effective hostname
    const orgHost = desktopOrgHost()
    if (orgHost) return orgHost

    // main webview (no org param): normalize the app:// hostname.
    // on Windows, WebView2 rewrites app:// to http://app.{host}/ so strip
    // the "app." prefix, then convert underscores back to dots
    return window.location.hostname.replace(/^app\./, "").replace(/_/g, ".")
  }

  // On web, allow overriding the hostname via DEV_FLAGS.TEST_ORG_HOST when running on localhost.
  // This allows testing org context and SSO flows on localhost:3000 without Firebase
  // domain validation issues.
  if (
    (window.location.hostname === "localhost" ||
      window.location.hostname.endsWith(".localhost")) &&
    DEV_FLAGS.TEST_ORG_HOST
  ) {
    return DEV_FLAGS.TEST_ORG_HOST
  }

  return window.location.hostname
}

const isDefaultHostname = (hostname: string): boolean =>
  pipe(
    [...DEFAULT_CLOUD_HOSTNAMES, ...API_DOCS_DOMAINS],
    A.some((domain) => domain === hostname)
  ) || isPRDeployPreview(hostname)

// This is **the** decision point for whether the app is running on default cloud
// (hoppscotch.io) or an org subdomain (acme.hoppscotch.io). Everything else in the UI
// keys off this value to determine what features to show. DEV_FLAGS.FORCE_ORG_MODE
// overrides the hostname check for testing purposes.
export const isDefaultCloudInstance: boolean = DEV_FLAGS.FORCE_ORG_MODE
  ? false
  : isDefaultHostname(getEffectiveHostname())

const extractOrgDomain = (hostname: string): O.Option<string> =>
  pipe(
    hostname.split("."),
    A.head,
    O.filter((domain) => domain.length > 0)
  )

/**
 * Returns the org subdomain like "acme" from "acme.hoppscotch.io", or null if on default
 * cloud. This is what determines which org's data gets fetched from the API throughout the
 * app. Works identically on web (acme.hoppscotch.io) and desktop (acme_hoppscotch_io) due
 * to the normalization that happens in getEffectiveHostname.
 */
export const getOrganizationDomain = (): string | null =>
  pipe(
    isDefaultCloudInstance,
    O.fromPredicate((isDefault) => !isDefault),
    O.chain(() => extractOrgDomain(getEffectiveHostname())),
    O.toNullable
  )

const extractRootDomain = (hostname: string): string =>
  hostname.split(".").slice(1).join(".")

/**
 * Returns the root domain like "hoppscotch.io" from "acme.hoppscotch.io". This is used
 * for building redirect URLs when switching between orgs. Returns empty string for default
 * instances since there's nothing to extract (hoppscotch.io has no parent domain).
 */
export const getRootDomain = (): string => {
  const hostname = getEffectiveHostname()

  if (isDefaultCloudInstance) {
    // default.hoppscotch.io is a special case used for local testing as a "default" instance
    return hostname === "default.hoppscotch.io" ? "default.hoppscotch.io" : ""
  }

  return extractRootDomain(hostname)
}

/**
 * Returns the full hostname including org subdomain (like "acme.hoppscotch.io"). Desktop
 * needs this to determine which backend to connect to since the org context affects which
 * GraphQL queries get made and what data gets returned. Returns null for default cloud.
 */
export const getOrganizationHost = (): string | null =>
  pipe(
    isDefaultCloudInstance,
    O.fromPredicate((isDefault) => !isDefault),
    O.map(() => getEffectiveHostname()),
    O.toNullable
  )

// on desktop, all webviews share the same app://hoppscotch origin (required
// for Tauri IPC to work). the org host is passed as a query param instead:
// app://hoppscotch/?org=test-org.hoppscotch.io
//
// this function extracts the org subdomain from that query param the same
// way the web path extracts it from window.location.hostname. it's sync
// (no store reads) and available immediately at module load time
export const getOrganizationDomainDesktop = (): string | null => {
  const orgHost = desktopOrgHost()
  if (!orgHost) return null
  return pipe(
    orgHost.split("."),
    A.head,
    O.filter((domain) => domain.length > 0),
    O.toNullable
  )
}

/**
 * Checks if currently running on a specific org instance by comparing the extracted org
 * domain with the provided one.
 */
export const isOrganizationInstance = (orgDomain: string): boolean =>
  pipe(
    getOrganizationDomain(),
    O.fromNullable,
    O.filter((current) => current === orgDomain),
    O.isSome
  )

// log module-level decisions (at the bottom so all consts are initialized)
diag("cloud-for-orgs", "--- cloud-for-orgs module evaluated ---")
diag("cloud-for-orgs", "isDesktopMode:", isDesktopMode())
diag("cloud-for-orgs", "desktopOrgHost():", desktopOrgHost() ?? "(null)")
diag("cloud-for-orgs", "getEffectiveHostname():", getEffectiveHostname())
diag("cloud-for-orgs", "isDefaultCloudInstance:", isDefaultCloudInstance)
diag(
  "cloud-for-orgs",
  "getOrganizationDomain():",
  getOrganizationDomain() ?? "(null)"
)
diag(
  "cloud-for-orgs",
  "getOrganizationHost():",
  getOrganizationHost() ?? "(null)"
)
diag("cloud-for-orgs", "DEV_FLAGS.FORCE_ORG_MODE:", DEV_FLAGS.FORCE_ORG_MODE)
